import React, { useState, useEffect } from 'react';
import { 
  Coins, Search, Send, User, RefreshCw, CheckCircle, 
  XCircle, AlertCircle, History, Sparkles, TrendingUp,
  ShieldCheck, ArrowUpRight, ArrowDownLeft, Clock, Award, FileText
} from 'lucide-react';
import { sellerAPI } from '../utils/api';

const SellerDashboard = () => {
  // Stats & Profile
  const [stats, setStats] = useState({
    current_balance: 0,
    total_sold: 0,
    total_transfers: 0,
    today_sold: 0,
    today_transfers: 0,
    seller_level: 'BRONZE',
    commission_percentage: 10
  });
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // User Search State
  const [userQuery, setUserQuery] = useState('');
  const [searchingUser, setSearchingUser] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  // Transfer Coins State
  const [transferAmount, setTransferAmount] = useState('');
  const [transferNote, setTransferNote] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferSuccessMsg, setTransferSuccessMsg] = useState('');
  const [transferErrorMsg, setTransferErrorMsg] = useState('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // Transactions History State
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDashboardData();
    fetchTransactions();
  }, []);

  // Real-time debounce search user
  useEffect(() => {
    if (!userQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(() => {
      searchTargetUser(userQuery.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [userQuery]);

  const fetchDashboardData = async () => {
    setRefreshing(true);
    try {
      const [statsRes, profileRes] = await Promise.all([
        sellerAPI.getDashboardStats(),
        sellerAPI.getProfile()
      ]);
      if (statsRes.data?.success) {
        setStats(statsRes.data.data);
      }
      if (profileRes.data?.success) {
        setSeller(profileRes.data.data);
      }
    } catch (err) {
      console.error("Error loading seller dashboard:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchTransactions = async () => {
    setLoadingTx(true);
    try {
      const res = await sellerAPI.getTransactions({ search: searchTerm });
      if (res.data?.success) {
        setTransactions(res.data.data || []);
      }
    } catch (err) {
      console.error("Error loading transactions:", err);
    } finally {
      setLoadingTx(false);
    }
  };

  const searchTargetUser = async (query) => {
    setSearchingUser(true);
    try {
      const res = await sellerAPI.searchUser(query);
      if (res.data?.success) {
        setSearchResults(res.data.data || []);
      }
    } catch (err) {
      console.error("Search user error:", err);
    } finally {
      setSearchingUser(false);
    }
  };

  // 🔹 Comma Formatting Helpers (Same as Admin Wallet Management)
  const formatNumberWithCommas = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const numStr = String(val).replace(/,/g, '').replace(/[^0-9]/g, '');
    if (!numStr) return '';
    return Number(numStr).toLocaleString('en-IN');
  };

  const getRawNumber = (val) => {
    if (!val) return 0;
    const numStr = String(val).replace(/,/g, '').replace(/[^0-9]/g, '');
    return Number(numStr) || 0;
  };

  const formatAmountInWords = (num) => {
    const n = Number(num);
    if (!n || n <= 0) return '';
    if (n >= 10000000) {
      const cr = (n / 10000000).toFixed(2).replace(/\.00$/, '');
      return `${cr} Crore Coins (${n.toLocaleString('en-IN')})`;
    }
    if (n >= 100000) {
      const lk = (n / 100000).toFixed(2).replace(/\.00$/, '');
      return `${lk} Lakh Coins (${n.toLocaleString('en-IN')})`;
    }
    if (n >= 1000) {
      const th = (n / 1000).toFixed(2).replace(/\.00$/, '');
      return `${th} Thousand Coins (${n.toLocaleString('en-IN')})`;
    }
    return `${n.toLocaleString('en-IN')} Coins`;
  };

  const handleAmountChange = (e) => {
    const raw = e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '');
    if (raw === '') {
      setTransferAmount('');
      return;
    }
    setTransferAmount(formatNumberWithCommas(raw));
  };

  const setQuickAmount = (val) => {
    setTransferAmount(formatNumberWithCommas(val));
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setUserQuery(`${user.name} (XOXO: ${user.xoxo_id || 'N/A'})`);
    setSearchResults([]);
    setTransferErrorMsg('');
  };

  const handleExecuteTransfer = async () => {
    const rawAmt = getRawNumber(transferAmount);
    if (!selectedUser || rawAmt <= 0) {
      setTransferErrorMsg("Please select a valid user and enter a valid coin amount.");
      return;
    }

    if (rawAmt > stats.current_balance) {
      setTransferErrorMsg(`Insufficient Balance! You have ${stats.current_balance.toLocaleString('en-IN')} Coins.`);
      return;
    }

    setTransferLoading(true);
    setTransferErrorMsg('');
    setTransferSuccessMsg('');

    try {
      const res = await sellerAPI.transferCoins({
        target_user_id: selectedUser.id,
        amount: rawAmt,
        note: transferNote.trim()
      });

      if (res.data?.success) {
        setIsConfirmModalOpen(false);
        setTransferSuccessMsg(res.data.message || `Successfully sent ${rawAmt.toLocaleString('en-IN')} coins to ${selectedUser.name}!`);
        setTransferAmount('');
        setTransferNote('');
        setSelectedUser(null);
        setUserQuery('');
        
        // Refresh balance & transactions
        fetchDashboardData();
        fetchTransactions();
      }
    } catch (err) {
      setTransferErrorMsg(err.response?.data?.message || err.message || "Failed to transfer coins.");
    } finally {
      setTransferLoading(false);
    }
  };

  return (
    <div className="space-y-8 font-['Outfit']">
      {/* Top Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-amber-400/10 border border-amber-400/30 text-amber-400 tracking-wide">
              <Sparkles size={12} /> VERIFIED COIN MERCHANT
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight">
            Seller Wallet & Coin Transfer
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Recharge user accounts directly from your merchant balance with instant delivery.
          </p>
        </div>

        <button
          onClick={() => { fetchDashboardData(); fetchTransactions(); }}
          disabled={refreshing}
          className="self-start md:self-auto flex items-center gap-2 px-5 py-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm transition"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin text-amber-400' : ''} />
          <span>Refresh Live Balance</span>
        </button>
      </div>

      {/* Hero Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* 1. Live Balance Card (Hero) */}
        <div className="md:col-span-2 bg-gradient-to-br from-amber-500/20 via-yellow-500/10 to-amber-950/40 border-2 border-amber-400/30 rounded-[30px] p-6 md:p-8 relative overflow-hidden shadow-2xl shadow-amber-500/10">
          <div className="absolute top-0 right-0 w-48 h-48 bg-amber-400/10 rounded-full blur-3xl pointer-events-none"></div>
          
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-2xl bg-amber-400/20 border border-amber-400/30 flex items-center justify-center">
                <Coins size={22} className="text-amber-400" />
              </div>
              <span className="text-xs font-black tracking-widest text-amber-300 uppercase">Available Merchant Coins</span>
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-black bg-amber-400 text-slate-950">
              LIVE
            </span>
          </div>

          <div className="flex items-baseline gap-2">
            <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">
              {Number(stats.current_balance).toLocaleString('en-IN')}
            </h2>
            <span className="text-lg font-bold text-amber-400">Coins</span>
          </div>

          <p className="text-xs text-amber-200/70 font-medium mt-2">
            {formatAmountInWords(stats.current_balance)}
          </p>

          <div className="mt-6 pt-5 border-t border-amber-400/20 flex items-center justify-between text-xs text-slate-300">
            <span>Seller Tier: <strong className="text-amber-400 font-extrabold">{stats.seller_level}</strong></span>
            <span>Commission Rate: <strong className="text-white font-extrabold">{stats.commission_percentage}%</strong></span>
          </div>
        </div>

        {/* 2. Today Sales */}
        <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-[30px] p-6 relative overflow-hidden flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between text-slate-400 mb-3">
              <span className="text-xs font-extrabold uppercase tracking-wider">Today's Sales</span>
              <div className="w-8 h-8 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center text-green-400">
                <TrendingUp size={16} />
              </div>
            </div>
            <h3 className="text-2xl font-black text-white">
              {Number(stats.today_sold).toLocaleString('en-IN')}
            </h3>
            <p className="text-xs text-green-400 font-bold mt-1">
              +{stats.today_transfers} Transfers Today
            </p>
          </div>
          <p className="text-[11px] text-slate-400 mt-4">
            Reset every midnight IST
          </p>
        </div>

        {/* 3. Total Sales (All Time) */}
        <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-[30px] p-6 relative overflow-hidden flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between text-slate-400 mb-3">
              <span className="text-xs font-extrabold uppercase tracking-wider">Total Sales</span>
              <div className="w-8 h-8 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                <Award size={16} />
              </div>
            </div>
            <h3 className="text-2xl font-black text-white">
              {Number(stats.total_sold).toLocaleString('en-IN')}
            </h3>
            <p className="text-xs text-purple-400 font-bold mt-1">
              {stats.total_transfers} Total Transfers
            </p>
          </div>
          <p className="text-[11px] text-slate-400 mt-4">
            Lifetime business performance
          </p>
        </div>
      </div>

      {/* Main Action Section: Direct Coin Transfer Card */}
      <div className="bg-slate-900/70 backdrop-blur-2xl border border-white/10 rounded-[32px] p-6 md:p-10 shadow-2xl relative">
        <div className="flex items-center gap-3 mb-6 pb-6 border-b border-white/10">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-amber-500 to-yellow-400 flex items-center justify-center text-slate-950 font-black">
            <Send size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-black text-white">Direct Add Coins to User</h2>
            <p className="text-sm text-slate-400">Search customer by XOXO ID, Phone or Name, enter amount and transfer instantly.</p>
          </div>
        </div>

        {/* Alerts */}
        {transferSuccessMsg && (
          <div className="mb-6 p-4 rounded-2xl bg-green-500/10 border border-green-500/25 text-green-400 font-bold flex items-center gap-3 animate-in fade-in">
            <CheckCircle size={22} className="shrink-0" />
            <span>{transferSuccessMsg}</span>
          </div>
        )}
        {transferErrorMsg && (
          <div className="mb-6 p-4 rounded-2xl bg-red-500/10 border border-red-500/25 text-red-400 font-bold flex items-center gap-3 animate-in fade-in">
            <AlertCircle size={22} className="shrink-0" />
            <span>{transferErrorMsg}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column: Search & User Selection */}
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-black text-slate-300 uppercase tracking-wider mb-2">
                1. Search Target User (XOXO ID / Phone / Name)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                  <Search size={18} />
                </div>
                <input
                  type="text"
                  value={userQuery}
                  onChange={(e) => {
                    setUserQuery(e.target.value);
                    if (selectedUser) setSelectedUser(null);
                  }}
                  placeholder="Type XOXO ID (e.g. 10001) or phone or username..."
                  className="w-full pl-11 pr-10 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition font-medium text-sm"
                />
                {searchingUser && (
                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                    <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>

              {/* Search Results Dropdown */}
              {searchResults.length > 0 && (
                <div className="mt-2 bg-[#0c1227] border border-white/10 rounded-2xl p-2 shadow-2xl space-y-1 max-h-60 overflow-y-auto">
                  {searchResults.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => handleSelectUser(u)}
                      className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition text-left group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center font-bold text-white overflow-hidden">
                          {u.avatar_url ? (
                            <img src={u.avatar_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            u.name[0]
                          )}
                        </div>
                        <div>
                          <p className="font-bold text-white text-sm group-hover:text-amber-400 transition">{u.name}</p>
                          <p className="text-xs text-slate-400">XOXO ID: <strong className="text-amber-400">{u.xoxo_id || 'N/A'}</strong></p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-slate-400">Balance:</span>
                        <p className="font-bold text-sm text-white">{Number(u.balance).toLocaleString('en-IN')} Coins</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected User Preview Card */}
            {selectedUser ? (
              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-2xl p-5 animate-in fade-in">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-black text-purple-300 uppercase tracking-wider flex items-center gap-1.5">
                    <CheckCircle size={14} className="text-green-400" /> Target Customer Selected
                  </span>
                  <button
                    type="button"
                    onClick={() => { setSelectedUser(null); setUserQuery(''); }}
                    className="text-xs text-slate-400 hover:text-white underline font-bold"
                  >
                    Change User
                  </button>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-purple-500 to-pink-500 p-[2px]">
                    <div className="w-full h-full bg-[#0a0f24] rounded-[14px] flex items-center justify-center overflow-hidden font-black text-white text-lg">
                      {selectedUser.avatar_url ? (
                        <img src={selectedUser.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        selectedUser.name[0]
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-black text-white text-lg">{selectedUser.name}</h3>
                    <p className="text-xs font-bold text-amber-400">XOXO ID: {selectedUser.xoxo_id || 'N/A'}</p>
                    <p className="text-xs text-slate-300 mt-0.5">Current Balance: <strong>{Number(selectedUser.balance).toLocaleString('en-IN')} Coins</strong></p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-white/10 rounded-2xl p-8 text-center text-slate-500">
                <User size={32} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm font-medium">Please search and select a customer first</p>
              </div>
            )}
          </div>

          {/* Right Column: Amount & Transfer Action */}
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-black text-slate-300 uppercase tracking-wider mb-2">
                2. Enter Coins Amount (Auto-Formatted with Commas)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-amber-400">
                  <Coins size={20} />
                </div>
                <input
                  type="text"
                  value={transferAmount}
                  onChange={handleAmountChange}
                  placeholder="e.g. 100,000"
                  className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white text-xl font-black placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition"
                />
              </div>

              {/* Amount in words live display */}
              {getRawNumber(transferAmount) > 0 && (
                <div className="mt-2 px-3 py-1.5 rounded-xl bg-amber-400/10 border border-amber-400/20 text-amber-400 text-xs font-bold flex items-center justify-between animate-in fade-in">
                  <span>Amount: {formatAmountInWords(getRawNumber(transferAmount))}</span>
                  <span>Clean: {getRawNumber(transferAmount)}</span>
                </div>
              )}
            </div>

            {/* Quick Amount Selection Chips */}
            <div>
              <label className="block text-xs font-extrabold text-slate-400 uppercase tracking-wider mb-2">
                Quick Select Slabs
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[10000, 50000, 100000, 500000, 1000000, 5000000].map((amt) => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setQuickAmount(amt)}
                    className="px-3 py-2.5 rounded-xl bg-white/5 hover:bg-amber-400/20 border border-white/10 hover:border-amber-400/30 text-xs font-bold text-slate-300 hover:text-amber-400 transition text-center"
                  >
                    +{amt.toLocaleString('en-IN')}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-black text-slate-300 uppercase tracking-wider mb-2">
                3. Note / Customer Remark (Optional)
              </label>
              <input
                type="text"
                value={transferNote}
                onChange={(e) => setTransferNote(e.target.value)}
                placeholder="e.g. UPI payment received / special recharge"
                className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 font-medium text-sm"
              />
            </div>

            <button
              type="button"
              disabled={!selectedUser || getRawNumber(transferAmount) <= 0 || getRawNumber(transferAmount) > stats.current_balance}
              onClick={() => setIsConfirmModalOpen(true)}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-slate-950 font-black text-base shadow-xl shadow-amber-500/25 hover:shadow-amber-500/40 hover:scale-[1.02] active:scale-[0.98] transition duration-200 flex items-center justify-center gap-2 disabled:opacity-40 disabled:pointer-events-none"
            >
              <Send size={18} />
              <span>Transfer {transferAmount ? transferAmount + ' Coins' : 'Coins'} Now</span>
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isConfirmModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-[#0e162f] border border-amber-500/30 rounded-[32px] p-8 max-w-md w-full shadow-2xl relative overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="w-16 h-16 rounded-3xl bg-amber-400/20 border border-amber-400/30 flex items-center justify-center mx-auto mb-5 text-amber-400">
              <Coins size={32} />
            </div>

            <h3 className="text-2xl font-black text-white text-center mb-2">Confirm Coin Transfer</h3>
            <p className="text-slate-400 text-sm text-center mb-6">
              Please double check recipient details before confirming.
            </p>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3 mb-6 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Customer:</span>
                <strong className="text-white">{selectedUser?.name}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">XOXO ID:</span>
                <strong className="text-amber-400">{selectedUser?.xoxo_id || 'N/A'}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Amount:</span>
                <strong className="text-xl font-black text-amber-400">{transferAmount} Coins</strong>
              </div>
              <div className="flex justify-between text-xs pt-2 border-t border-white/10">
                <span className="text-slate-400">Your New Balance:</span>
                <span className="font-bold text-white">{(stats.current_balance - getRawNumber(transferAmount)).toLocaleString('en-IN')} Coins</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setIsConfirmModalOpen(false)}
                className="py-3.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm transition"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={transferLoading}
                onClick={handleExecuteTransfer}
                className="py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-black text-sm shadow-lg shadow-amber-500/25 hover:scale-[1.02] active:scale-[0.98] transition flex items-center justify-center gap-2"
              >
                {transferLoading ? (
                  <div className="w-5 h-5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    <span>Confirm & Send</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction & Sales History Table */}
      <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-[32px] p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-slate-300">
              <History size={20} />
            </div>
            <div>
              <h3 className="text-xl font-black text-white">Coin Sales History</h3>
              <p className="text-xs text-slate-400">Recent coin transfer logs and customer recharges</p>
            </div>
          </div>

          <div className="relative min-w-[260px]">
            <Search size={16} className="absolute inset-y-0 left-3 my-auto text-slate-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchTransactions()}
              placeholder="Search history..."
              className="w-full pl-9 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-xs placeholder-slate-500 focus:outline-none focus:border-amber-400"
            />
          </div>
        </div>

        {loadingTx ? (
          <div className="py-12 text-center text-slate-500">
            <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-sm font-medium">Loading transaction records...</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="py-12 text-center text-slate-500">
            <Coins size={36} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm font-medium">No coin transactions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-300">
              <thead className="bg-white/5 text-[11px] font-black uppercase tracking-wider text-slate-400 border-b border-white/10">
                <tr>
                  <th className="p-4 rounded-l-xl">Type</th>
                  <th className="p-4">Details / Description</th>
                  <th className="p-4">Coins Amount</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 rounded-r-xl">Date & Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {transactions.map((tx) => {
                  const isDeduct = tx.transaction_type === 'COIN_DEDUCT';
                  return (
                    <tr key={tx.id} className="hover:bg-white/5 transition duration-150">
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                            isDeduct 
                              ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25' 
                              : 'bg-green-500/15 text-green-400 border border-green-500/25'
                          }`}>
                            {isDeduct ? <ArrowUpRight size={16} /> : <ArrowDownLeft size={16} />}
                          </div>
                          <span className="font-bold text-xs text-white">
                            {isDeduct ? 'COIN SALE' : 'RECHARGE'}
                          </span>
                        </div>
                      </td>
                      <td className="p-4 max-w-md">
                        <p className="font-medium text-slate-200 text-xs leading-relaxed">{tx.description}</p>
                      </td>
                      <td className="p-4 font-black text-sm">
                        <span className={isDeduct ? 'text-amber-400' : 'text-green-400'}>
                          {isDeduct ? '-' : '+'}{Number(tx.amount).toLocaleString('en-IN')} Coins
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black bg-green-500/10 border border-green-500/25 text-green-400">
                          <CheckCircle size={10} /> COMPLETED
                        </span>
                      </td>
                      <td className="p-4 text-xs text-slate-400">
                        {tx.created_at ? new Date(tx.created_at).toLocaleString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        }) : 'N/A'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerDashboard;
